import React, { useCallback, useEffect, useRef, useState } from "react";
import { tradeGet, tradeJson } from "../../utils/tradeApi";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseAddresses(value) {
  return [...new Set(String(value || "").split(/[,;\n]/).map((item) => item.trim().toLowerCase()).filter(Boolean))];
}

function defaultAssignments(options, fileIds) {
  const selectedCategories = new Set();
  (options?.categories || []).forEach((category) => {
    if ((category.files || []).some((file) => fileIds.includes(file.id))) selectedCategories.add(category.id);
  });
  return Object.fromEntries((options?.subscribers || []).map((subscriber) => {
    const receivesSelectedCategory = (subscriber.subscriptions || []).some(
      (subscription) => selectedCategories.has(subscription.categoryId) && subscription.receiveNotifications,
    );
    return [subscriber.id, receivesSelectedCategory ? "to" : "none"];
  }));
}

export default function TradeManualNoticeEditor({ projectId, onCompleted, initialDraft = null }) {
  const [options, setOptions] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [manualTo, setManualTo] = useState("");
  const [manualCc, setManualCc] = useState("");
  const [subject, setSubject] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [failedBatchId, setFailedBatchId] = useState("");
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const sendInFlight = useRef(false);

  const loadOptions = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const result = await tradeGet(`/api/trade/notifications/options?projectId=${projectId}`);
      setOptions(result);
      return result;
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => {
    setSelectedFileIds([]); setAssignments({}); setManualTo(""); setManualCc("");
    setSubject(""); setPreview(null); setSuccess(""); setFailedBatchId(""); setSubscriberSearch("");
    let active = true;
    loadOptions().then((loadedOptions) => {
      if (!active || !loadedOptions || !initialDraft) return;
      const availableIds = new Set((loadedOptions.categories || []).flatMap((item) => (item.files || []).map((file) => file.id)));
      setSelectedFileIds((initialDraft.fileIds || []).filter((id) => availableIds.has(id)));
      setAssignments({});
      setManualTo((initialDraft.to || []).join(", "));
      setManualCc((initialDraft.cc || []).join(", "));
      setSubject(initialDraft.subject || "");
    });
    return () => { active = false; };
  }, [loadOptions, initialDraft]);

  const toSubscriberIds = Object.entries(assignments).filter(([, value]) => value === "to").map(([id]) => id);
  const ccSubscriberIds = Object.entries(assignments).filter(([, value]) => value === "cc").map(([id]) => id);
  const manualToAddresses = parseAddresses(manualTo);
  const manualCcAddresses = parseAddresses(manualCc).filter((address) => !manualToAddresses.includes(address));
  const invalidAddresses = [...manualToAddresses, ...manualCcAddresses].filter((address) => !EMAIL_PATTERN.test(address));
  const selectedSubscriberEmails = (options?.subscribers || [])
    .filter((subscriber) => assignments[subscriber.id] === "to").map((subscriber) => subscriber.email);
  const estimatedToCount = new Set([...selectedSubscriberEmails, ...manualToAddresses]).size;
  const canPreview = selectedFileIds.length > 0 && estimatedToCount > 0 && invalidAddresses.length === 0;
  const visibleSubscribers = (options?.subscribers || []).filter((subscriber) => {
    const search = subscriberSearch.trim().toLowerCase();
    return !search || [subscriber.name, subscriber.email, subscriber.company]
      .some((value) => String(value || "").toLowerCase().includes(search));
  });

  function invalidatePreview() { setPreview(null); setSuccess(""); setFailedBatchId(""); }

  function toggleFile(fileId) {
    const next = selectedFileIds.includes(fileId)
      ? selectedFileIds.filter((value) => value !== fileId)
      : [...selectedFileIds, fileId];
    setSelectedFileIds(next);
    setAssignments(defaultAssignments(options, next));
    invalidatePreview();
  }

  function toggleCategory(category) {
    const categoryIds = (category.files || []).map((file) => file.id);
    const allSelected = categoryIds.length > 0 && categoryIds.every((id) => selectedFileIds.includes(id));
    const next = allSelected
      ? selectedFileIds.filter((id) => !categoryIds.includes(id))
      : [...new Set([...selectedFileIds, ...categoryIds])].slice(0, options?.limits?.maxFiles || 50);
    setSelectedFileIds(next);
    setAssignments(defaultAssignments(options, next));
    invalidatePreview();
  }

  function updateAssignment(subscriberId, value) {
    setAssignments((current) => ({ ...current, [subscriberId]: value }));
    invalidatePreview();
  }

  function requestPayload(noticeRequestId) {
    return {
      projectId,
      fileIds: selectedFileIds,
      toSubscriberIds,
      ccSubscriberIds,
      to: manualToAddresses,
      cc: manualCcAddresses,
      ...(subject.trim() ? { subject: subject.trim() } : {}),
      ...(noticeRequestId ? { noticeRequestId } : {}),
    };
  }

  async function createPreview() {
    if (!canPreview) return;
    setLoading(true); setError(""); setSuccess(""); setFailedBatchId("");
    try {
      const result = await tradeJson("/api/trade/notifications/manual-preview", "POST", requestPayload());
      setPreview(result);
      setSubject(result.subject || "");
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }

  async function send() {
    if (!preview?.noticeRequestId || sendInFlight.current) return;
    sendInFlight.current = true; setSending(true); setError(""); setSuccess("");
    try {
      await tradeJson(
        "/api/trade/notifications/manual-send",
        "POST",
        requestPayload(preview.noticeRequestId),
      );
      setSuccess("Manual notice sent successfully.");
      setSelectedFileIds([]); setAssignments({}); setManualTo(""); setManualCc("");
      setSubject(""); setPreview(null); setFailedBatchId("");
      await loadOptions();
      await onCompleted();
    } catch (requestError) {
      setError(requestError.message);
      if (requestError.data?.retryable && requestError.data?.batchId) {
        setFailedBatchId(requestError.data.batchId);
        await onCompleted();
      }
    }
    finally { sendInFlight.current = false; setSending(false); }
  }

  async function retryFailed() {
    if (!failedBatchId || sendInFlight.current) return;
    sendInFlight.current = true; setSending(true); setError("");
    try {
      await tradeJson(`/api/trade/notifications/${failedBatchId}/retry`, "POST");
      setSuccess("Manual notice sent successfully after retry.");
      setSelectedFileIds([]); setAssignments({}); setManualTo(""); setManualCc("");
      setSubject(""); setPreview(null); setFailedBatchId("");
      await loadOptions();
      await onCompleted();
    } catch (requestError) { setError(requestError.message); }
    finally { sendInFlight.current = false; setSending(false); }
  }

  if (loading && !options) return <div className="trade-loading">Loading manual notice editor…</div>;

  return (
    <div className="trade-manual-editor">
      <p className="trade-muted">Choose current files from one or more drawing packages. Every To and CC recipient receives links to every selected file.</p>
      {error && <div className="trade-alert error">{error}{failedBatchId && <button className="trade-link-button" onClick={retryFailed} disabled={sending}>{sending ? "Retrying…" : "Retry failed notice"}</button>}</div>}
      {success && <div className="trade-alert success">{success}</div>}

      {!preview && options && <>
        <section className="trade-manual-section">
          <div className="trade-manual-heading"><strong>1. Select drawings</strong><span>{selectedFileIds.length} of {options.limits?.maxFiles || 50} selected</span></div>
          <div className="trade-manual-categories">
            {(options.categories || []).map((category) => (
              <fieldset key={category.id}>
                <legend><span>{category.code}</span>{category.name}<button type="button" className="trade-link-button" onClick={() => toggleCategory(category)} disabled={!(category.files || []).length}>{(category.files || []).length > 0 && (category.files || []).every((file) => selectedFileIds.includes(file.id)) ? "Clear category" : "Select category"}</button></legend>
                {!(category.files || []).length && <small>No available files.</small>}
                {(category.files || []).map((file) => (
                  <label className="trade-manual-file" key={file.id}>
                    <input type="checkbox" checked={selectedFileIds.includes(file.id)} onChange={() => toggleFile(file.id)} />
                    <span><strong>{file.display_name}</strong><small>{file.issue_date || "No issue date"} · {file.status}</small></span>
                  </label>
                ))}
              </fieldset>
            ))}
          </div>
          {selectedFileIds.length > 0 && <div className="trade-selected-files"><strong>Selected:</strong> {(options.categories || []).flatMap((item) => item.files || []).filter((file) => selectedFileIds.includes(file.id)).map((file) => file.display_name).join(", ")}</div>}
        </section>

        <section className="trade-manual-section">
          <div className="trade-manual-heading"><strong>2. Choose project participants</strong><span>{estimatedToCount} To recipient{estimatedToCount === 1 ? "" : "s"}</span></div>
          <label className="trade-subscriber-search">Search participants<input type="search" value={subscriberSearch} onChange={(event) => setSubscriberSearch(event.target.value)} placeholder="Name, email, or company" /></label>
          <div className="trade-manual-subscribers">
            {visibleSubscribers.map((subscriber) => (
              <label key={subscriber.id}>
                <span><strong>{subscriber.name || subscriber.email}</strong><small>{subscriber.email}{subscriber.company ? ` · ${subscriber.company}` : ""}</small></span>
                <select aria-label={`${subscriber.email} recipient type`} value={assignments[subscriber.id] || "none"} onChange={(event) => updateAssignment(subscriber.id, event.target.value)}>
                  <option value="none">Do not send</option><option value="to">To</option><option value="cc">CC</option>
                </select>
              </label>
            ))}
            {!(options.subscribers || []).length && <div className="trade-empty small">No active project subscribers.</div>}
            {(options.subscribers || []).length > 0 && !visibleSubscribers.length && <div className="trade-empty small">No matching project participants.</div>}
          </div>
        </section>

        <section className="trade-manual-section trade-email-fields">
          <div className="trade-manual-heading"><strong>3. Add email addresses</strong><span>Optional</span></div>
          <label>Additional To<textarea aria-label="Additional To" value={manualTo} onChange={(event) => { setManualTo(event.target.value); invalidatePreview(); }} rows="2" placeholder="recipient@example.com" /></label>
          <label>Additional CC<textarea aria-label="Additional CC" value={manualCc} onChange={(event) => { setManualCc(event.target.value); invalidatePreview(); }} rows="2" placeholder="copy@example.com" /></label>
          <label>Subject<input value={subject} onChange={(event) => { setSubject(event.target.value); invalidatePreview(); }} maxLength={options.limits?.maxSubjectLength || 200} placeholder="Leave blank to use the project and discipline default" /></label>
          <small>Separate addresses with commas, semicolons, or new lines. An address in To is automatically removed from CC.</small>
          {invalidAddresses.length > 0 && <div className="trade-alert warning">Check these email addresses: {invalidAddresses.join(", ")}</div>}
        </section>

        <div className="trade-action-row"><button className="trade-button primary" onClick={createPreview} disabled={!canPreview || loading}>{loading ? "Preparing…" : "Preview manual notice"}</button></div>
      </>}

      {preview && <div className="trade-manual-preview">
        <div className="trade-manual-heading"><strong>Manual notice preview</strong><span>{preview.files?.length || 0} file(s) · {preview.disciplines?.map((item) => item.name).join(", ")}</span></div>
        <dl><div><dt>To</dt><dd>{(preview.to || []).join(", ")}</dd></div><div><dt>CC</dt><dd>{(preview.cc || []).join(", ") || "—"}</dd></div><div><dt>Subject</dt><dd>{preview.subject}</dd></div></dl>
        <iframe title="Manual notice email preview" sandbox="" srcDoc={preview.html || ""} />
        <details><summary>Plain-text version</summary><pre>{preview.text}</pre></details>
        <div className="trade-action-row"><button className="trade-button secondary" onClick={() => { setPreview(null); setFailedBatchId(""); }} disabled={sending || Boolean(failedBatchId)}>Back to edit</button><button className="trade-button primary" onClick={send} disabled={sending || Boolean(failedBatchId)}>{sending ? "Sending…" : `Send manual notice to ${(preview.to || []).length} recipient${(preview.to || []).length === 1 ? "" : "s"}`}</button></div>
      </div>}
    </div>
  );
}
